import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { GetOrganizationsResult } from "@src/application/useCases/organization/GetOrganizations.useCase";
import type { Organization } from "@src/domain/entities/organization";
import { Brand, type BrandUser } from "@src/domain/entities/brand";
import { AppConfig } from "@src/config/AppConfig";
import {
  cloneOrganizationWithBrands,
  isUsableOrganization,
  normalizeOrganizationCacheKey,
  organizationKeysMatch,
  persistOrganizationIdForKey,
} from "@src/presentation/pages/Organizations/utils/organizationDetailsResolve";
import { countActiveBrandFeatures } from "@src/presentation/pages/Organizations/utils/brandFeatureUtils";

/** Base shape for fetch state: data, loading, error + setters. */
export interface FetchSlice<T = unknown> {
  data: T | null;
  setData: React.Dispatch<React.SetStateAction<T | null>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

/** Cache key for a single page: "page-pageSize". */
function pageCacheKey(page: number, pageSize: number): string {
  return `${page}-${pageSize}`;
}

/**
 * Module-level page cache so it survives navigation.
 * When the route unmounts, OrganizationsDataProvider unmounts and loses React state/refs;
 * this Map stays so when we come back we can serve from cache and avoid refetch and re-renders.
 */
const persistentPageCache = new Map<string, GetOrganizationsResult>();

/** Clear the cache when route navigation happens (provider unmounts on page leave). */
function clearPersistentPageCache(): void {
  persistentPageCache.clear();
}

const persistentDetailsCache = new Map<string, Organization>();

function clearPersistentDetailsCache(): void {
  persistentDetailsCache.clear();
}

const persistentBrandDetailsCache = new Map<number, Brand>();

function clearPersistentBrandDetailsCache(): void {
  persistentBrandDetailsCache.clear();
}

function brandsContainBrandId(
  brands: { brandId: number }[],
  brandId: number,
): boolean {
  return brands.some((b) => b.brandId === brandId);
}

function addKnownOrganizationKey(
  keys: Set<string>,
  key: string | null | undefined,
): void {
  const trimmed = key?.trim();
  if (trimmed) keys.add(trimmed.toUpperCase());
}

function addKnownBrandKeysFromOrganization(
  keys: Set<string>,
  org: Organization | null | undefined,
): void {
  if (!org) return;
  for (const brand of org.brands ?? []) {
    const key = brand.brandKey?.trim();
    if (key) keys.add(key.toUpperCase());
  }
}

/** All brand keys from every org on loaded list pages and cached detail visits. */
function collectKnownBrandKeys(
  listData: GetOrganizationsResult | null,
  organizationDetails: Organization | null,
): ReadonlySet<string> {
  const keys = new Set<string>();

  addKnownBrandKeysFromOrganization(keys, organizationDetails);

  for (const org of listData?.items ?? []) {
    addKnownBrandKeysFromOrganization(keys, org);
  }
  for (const cached of persistentPageCache.values()) {
    for (const org of cached.items ?? []) {
      addKnownBrandKeysFromOrganization(keys, org);
    }
  }
  for (const org of persistentDetailsCache.values()) {
    addKnownBrandKeysFromOrganization(keys, org);
  }

  return keys;
}

/** Union of organization keys from the current list and session caches (best-effort). */
function collectKnownOrganizationKeys(
  listData: GetOrganizationsResult | null,
): ReadonlySet<string> {
  const keys = new Set<string>();
  for (const org of listData?.items ?? []) {
    addKnownOrganizationKey(keys, org.organizationKey);
  }
  for (const cached of persistentPageCache.values()) {
    for (const org of cached.items ?? []) {
      addKnownOrganizationKey(keys, org.organizationKey);
    }
  }
  for (const [cacheKey, org] of persistentDetailsCache) {
    addKnownOrganizationKey(keys, cacheKey);
    addKnownOrganizationKey(keys, org.organizationKey);
  }
  return keys;
}

function mapBrandsWithUpdatedOne(
  brands: Brand[],
  brandId: number,
  mergeBrand: (existing: Brand) => Brand,
): Brand[] {
  return brands.map((b) => (b.brandId === brandId ? mergeBrand(b) : b));
}

/** Organizations slice: fetch state + pagination + per-page cache + details. */
export interface OrganizationsSlice extends FetchSlice<GetOrganizationsResult> {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (size: number) => void;
  getCachedPage: (
    page: number,
    pageSize: number,
  ) => GetOrganizationsResult | null;
  setCachedPage: (
    page: number,
    pageSize: number,
    result: GetOrganizationsResult,
  ) => void;
  organizationDetails: Organization | null;
  setOrganizationDetails: React.Dispatch<
    React.SetStateAction<Organization | null>
  >;
  organizationDetailsLoading: boolean;
  setOrganizationDetailsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  organizationDetailsError: string | null;
  setOrganizationDetailsError: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  organizationDetailsKey: string | null;
  setOrganizationDetailsKey: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  getCachedDetails: (organizationKey: string) => Organization | null;
  setCachedDetails: (
    organizationKey: string,
    organization: Organization,
  ) => void;
  getCachedBrandDetails: (brandId: number) => Brand | null;
  setCachedBrandDetails: (brandId: number, brand: Brand) => void;
  /** Apply a brand update to both list and details state so both pages stay in sync. */
  applyBrandUpdate: (updatedBrand: Brand) => void;
  /** Apply a brand user update to organization details state so brand details stay in sync. */
  applyBrandUserUpdate: (brandId: number, updatedUser: BrandUser) => void;
  /** Keys from loaded list pages and cached detail visits (not all orgs in the system). */
  getKnownOrganizationKeys: () => ReadonlySet<string>;
  /** Brand keys from all orgs in loaded list pages and cached detail visits. */
  getKnownBrandKeys: () => ReadonlySet<string>;
  /** Insert a newly created organization into list state (page 1 items) and details cache. */
  applyOrganizationCreate: (created: Organization) => void;
  /** Append a newly created brand to organization details and list caches. */
  applyBrandCreate: (organizationKey: string, created: Brand) => void;
  /** Merge full brand details after REST save (add/edit). */
  applyBrandDetailsSave: (updated: Brand, organizationKey?: string) => void;
}

const OrganizationsDataContext = createContext<OrganizationsSlice | null>(null);

function useOrganizationsDataContext(): OrganizationsSlice {
  const value = useContext(OrganizationsDataContext);
  if (value === null) {
    throw new Error(
      "useOrganizationsData must be used within an OrganizationsDataProvider",
    );
  }
  return value;
}

/**
 * Organizations data hook. Use within the Organizations page to read/update organizations state.
 *
 * Example:
 *   const { data, setData, loading, error, currentPage, setCurrentPage } = useOrganizationsData();
 */
export function useOrganizationsData(): OrganizationsSlice {
  return useOrganizationsDataContext();
}

/** Options for fetch (e.g. force refetch from server by evicting Apollo cache). */
export interface FetchOptions {
  refetch?: boolean;
  /** Keep current list visible while refreshing (no full-page loading state). */
  silent?: boolean;
}

/** Fetch function type: async, returns data or null. Optionally accepts refetch. */
export type FetchFn<T = unknown> = (
  options?: FetchOptions,
) => Promise<T | null>;

/**
 * Organizations fetch hook. Use on the Organizations page to load organizations from API.
 *
 * Cache flow (performance):
 * 1. Provider cache (per page) is checked first → instant UI when hit.
 * 2. On miss, fetchFn runs → repository uses Apollo client.query() (cache-first).
 * 3. Apollo returns from its cache when the same page was fetched before → no network.
 * 4. Only when both caches miss does the request go to the network.
 *
 * Refetch: pass refetch: true to fetchFn → repository calls invalidateOrganizationsCache()
 * (flush Apollo cache) then fetches → fresh data from server.
 *
 * Cache invalidation after mutations: for any successful API mutation that affects the
 * organizations list (e.g. brand status, organization update, add/remove brand), call
 * OrganizationRepository.invalidateOrganizationsCache() after success. That way when
 * the user leaves and comes back, the list refetches from the server. The page that
 * performs the mutation should also patch provider cache (setData/setCachedPage) when
 * still on the list so the UI updates immediately without refetch.
 *
 * @param fetchFn - async function that returns the organizations data (e.g. use case + repository)
 * @param deps - dependency array [currentPage, itemsPerPage]; refetch when these change
 * @returns refetch - call to run fetch again (e.g. after mutation). Use `{ silent: true }` after create/update to avoid swapping the list for a spinner.
 */
export function useFetchOrganizationsData(
  fetchFn: FetchFn<GetOrganizationsResult>,
  deps: React.DependencyList,
): { refetch: (options?: Pick<FetchOptions, "silent">) => Promise<void> } {
  const slice = useOrganizationsDataContext();
  const {
    setData,
    setLoading,
    setError,
    currentPage,
    itemsPerPage,
    getCachedPage,
    setCachedPage,
  } = slice;

  const runFetch = useCallback(
    async (options?: {
      isCancelled?: () => boolean;
      refetch?: boolean;
      silent?: boolean;
    }) => {
      if (!options?.silent) {
        setLoading(true);
      }
      setError(null);
      try {
        const result = await fetchFn({
          refetch: options?.refetch,
          silent: options?.silent,
        });
        if (options?.isCancelled?.()) return;
        if (result) setCachedPage(currentPage, itemsPerPage, result);
        setData(result ?? null);
      } catch (err) {
        if (options?.isCancelled?.()) return;
        const message =
          err instanceof Error ? err.message : "Failed to load data";
        setError(message);
        setData(null);
      } finally {
        if (!options?.isCancelled?.()) {
          setLoading(false);
        }
      }
    },
    [
      fetchFn,
      currentPage,
      itemsPerPage,
      setData,
      setLoading,
      setError,
      setCachedPage,
    ],
  );

  useEffect(() => {
    const cached = getCachedPage(currentPage, itemsPerPage);
    if (cached !== null) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }
    let ignore = false;
    runFetch({ isCancelled: () => ignore });
    return () => {
      ignore = true;
    };
  }, deps);

  const refetch = useCallback(
    (options?: Pick<FetchOptions, "silent">) =>
      runFetch({ refetch: true, silent: options?.silent }),
    [runFetch],
  );

  return { refetch };
}

/** Fetch function for organization details by key. */
export type FetchOrganizationDetailsFn = (
  organizationKey: string,
) => Promise<Organization | null>;

type OrganizationDetailsSetters = {
  setCachedDetails: (key: string, org: Organization) => void;
  setOrganizationDetails: React.Dispatch<
    React.SetStateAction<Organization | null>
  >;
  setOrganizationDetailsKey: React.Dispatch<
    React.SetStateAction<string | null>
  >;
};

function organizationMatchesFetchKey(
  routeKey: string,
  detailsKey: string | null,
  org: Organization | null | undefined,
): org is Organization {
  return (
    isUsableOrganization(org) &&
    (detailsKey === routeKey ||
      organizationKeysMatch(org.organizationKey, routeKey))
  );
}

function storeOrganizationDetails(
  routeKey: string,
  org: Organization,
  setters: OrganizationDetailsSetters,
): void {
  setters.setCachedDetails(routeKey, org);
  setters.setOrganizationDetails(org);
  setters.setOrganizationDetailsKey(routeKey);
}

function restoreOrClearOrganizationDetails(
  routeKey: string,
  detailsKey: string | null,
  existing: Organization | null,
  setters: OrganizationDetailsSetters,
): void {
  if (organizationMatchesFetchKey(routeKey, detailsKey, existing)) {
    setters.setOrganizationDetails(existing);
    setters.setOrganizationDetailsKey(routeKey);
    setters.setCachedDetails(routeKey, existing);
    return;
  }
  setters.setOrganizationDetails(null);
  setters.setOrganizationDetailsKey(null);
}

/**
 * Fetches and caches organization details. Use on the OrganizationDetails page.
 * Checks cache first; on miss runs fetchFn(organizationKey) and stores result in provider + persistent cache.
 */
export function useFetchOrganizationDetails(
  fetchFn: FetchOrganizationDetailsFn,
  organizationKey: string | undefined,
): { refetch: () => Promise<void> } {
  const slice = useOrganizationsDataContext();
  const {
    getCachedDetails,
    setCachedDetails,
    setOrganizationDetails,
    setOrganizationDetailsLoading,
    setOrganizationDetailsError,
    setOrganizationDetailsKey,
    organizationDetails,
    organizationDetailsKey,
  } = slice;

  const runFetch = useCallback(
    async (key: string, options?: { isCancelled?: () => boolean }) => {
      const isCancelled = () => Boolean(options?.isCancelled?.());
      const setters: OrganizationDetailsSetters = {
        setCachedDetails,
        setOrganizationDetails,
        setOrganizationDetailsKey,
      };

      setOrganizationDetailsLoading(true);
      setOrganizationDetailsError(null);
      try {
        const cached = getCachedDetails(key);
        if (isUsableOrganization(cached)) {
          if (isCancelled()) return;
          storeOrganizationDetails(key, cached, setters);
          setOrganizationDetailsLoading(false);
          return;
        }

        const result = await fetchFn(key);
        if (isCancelled()) return;

        if (isUsableOrganization(result)) {
          storeOrganizationDetails(key, result, setters);
        } else {
          restoreOrClearOrganizationDetails(
            key,
            organizationDetailsKey,
            organizationDetails,
            setters,
          );
        }
      } catch (err) {
        if (isCancelled()) return;
        const message =
          err instanceof Error ? err.message : "Failed to load organization";
        setOrganizationDetailsError(message);
        if (
          !organizationMatchesFetchKey(
            key,
            organizationDetailsKey,
            organizationDetails,
          )
        ) {
          setOrganizationDetails(null);
          setOrganizationDetailsKey(null);
        }
      } finally {
        if (!isCancelled()) {
          setOrganizationDetailsLoading(false);
        }
      }
    },
    [
      fetchFn,
      getCachedDetails,
      setCachedDetails,
      setOrganizationDetails,
      setOrganizationDetailsLoading,
      setOrganizationDetailsError,
      setOrganizationDetailsKey,
      organizationDetails,
      organizationDetailsKey,
    ],
  );

  useEffect(() => {
    if (!organizationKey) return;
    let ignore = false;
    runFetch(organizationKey, { isCancelled: () => ignore });
    return () => {
      ignore = true;
    };
  }, [organizationKey, runFetch]);

  const refetch = useCallback(() => {
    if (organizationKey) return runFetch(organizationKey);
    return Promise.resolve();
  }, [organizationKey, runFetch]);

  return { refetch };
}

interface OrganizationsDataProviderProps {
  children: ReactNode;
  defaultOrganizationsPageSize?: number;
}

export function OrganizationsDataProvider({
  children,
  defaultOrganizationsPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<OrganizationsDataProviderProps>) {
  const [organizationsData, setOrganizationsData] =
    useState<GetOrganizationsResult | null>(null);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizationsError, setOrganizationsError] = useState<string | null>(
    null,
  );
  const [organizationsCurrentPage, setOrganizationsCurrentPage] = useState(1);
  const [organizationsItemsPerPage, setOrganizationsItemsPerPage] = useState(
    defaultOrganizationsPageSize,
  );
  const [organizationDetails, setOrganizationDetails] =
    useState<Organization | null>(null);
  const [organizationDetailsLoading, setOrganizationDetailsLoading] =
    useState(false);
  const [organizationDetailsError, setOrganizationDetailsError] = useState<
    string | null
  >(null);
  const [organizationDetailsKey, setOrganizationDetailsKey] = useState<
    string | null
  >(null);

  const getCachedPage = useCallback(
    (page: number, pageSize: number): GetOrganizationsResult | null => {
      return persistentPageCache.get(pageCacheKey(page, pageSize)) ?? null;
    },
    [],
  );

  const setCachedPage = useCallback(
    (page: number, pageSize: number, result: GetOrganizationsResult) => {
      persistentPageCache.set(pageCacheKey(page, pageSize), result);
    },
    [],
  );

  const getCachedDetails = useCallback(
    (organizationKey: string): Organization | null => {
      const direct = persistentDetailsCache.get(organizationKey);
      if (direct) return direct;
      const target = normalizeOrganizationCacheKey(organizationKey);
      for (const [cacheKey, org] of persistentDetailsCache) {
        if (normalizeOrganizationCacheKey(cacheKey) === target) return org;
        if (organizationKeysMatch(org.organizationKey, organizationKey)) {
          return org;
        }
      }
      return null;
    },
    [],
  );

  const setCachedDetails = useCallback(
    (organizationKey: string, organization: Organization) => {
      persistentDetailsCache.set(organizationKey, organization);
      persistOrganizationIdForKey(
        organizationKey,
        organization.organizationId,
      );
      if (
        organization.organizationKey &&
        !organizationKeysMatch(organizationKey, organization.organizationKey)
      ) {
        persistOrganizationIdForKey(
          organization.organizationKey,
          organization.organizationId,
        );
      }
    },
    [],
  );

  const getCachedBrandDetails = useCallback((brandId: number): Brand | null => {
    return persistentBrandDetailsCache.get(brandId) ?? null;
  }, []);

  const setCachedBrandDetails = useCallback((brandId: number, brand: Brand) => {
    persistentBrandDetailsCache.set(brandId, brand);
  }, []);

  const applyBrandUpdate = useCallback(
    (updatedBrand: Brand) => {
      const brandId = updatedBrand.brandId;

      const mergeBrand = (existing: Brand): Brand => {
        const nextFeatures =
          updatedBrand.brandFeatures ?? existing.brandFeatures;
        return Brand.withProps(existing, {
          brandIsActive: updatedBrand.brandIsActive,
          brandFeaturesCount:
            updatedBrand.brandFeaturesCount ??
            countActiveBrandFeatures(nextFeatures),
          brandFeatures: nextFeatures,
        });
      };

      /** Update all cached organization details entries that contain this brand. */
      persistentDetailsCache.forEach((org, key) => {
        if (!org.brands?.length) return;
        if (!brandsContainBrandId(org.brands, brandId)) return;
        const brands = mapBrandsWithUpdatedOne(org.brands, brandId, mergeBrand);
        const next = cloneOrganizationWithBrands(org, brands);
        setCachedDetails(key, next);
      });

      /** Update the in-memory organizationDetails state if it includes this brand. */
      setOrganizationDetails((prev) => {
        if (!prev?.brands?.length) return prev;
        if (!brandsContainBrandId(prev.brands, brandId)) return prev;
        const brands = mapBrandsWithUpdatedOne(
          prev.brands,
          brandId,
          mergeBrand,
        );
        return cloneOrganizationWithBrands(prev, brands);
      });

      setOrganizationsData((prev) => {
        if (!prev?.items?.length) return prev;
        const applyBrandToOrganization = (org: unknown): unknown => {
          const o = org as { brands?: Brand[] };
          const orgBrands = o.brands ?? [];
          if (!brandsContainBrandId(orgBrands, brandId)) return org;
          const brands = mapBrandsWithUpdatedOne(
            orgBrands,
            brandId,
            mergeBrand,
          );
          return { ...o, brands };
        };
        const items = (prev.items as unknown[]).map(applyBrandToOrganization);
        const next = { ...prev, items } as GetOrganizationsResult;
        setCachedPage(
          organizationsCurrentPage,
          organizationsItemsPerPage,
          next,
        );
        return next;
      });
    },
    [
      organizationDetailsKey,
      organizationsCurrentPage,
      organizationsItemsPerPage,
      setCachedDetails,
      setCachedPage,
      setOrganizationDetails,
      setOrganizationDetailsKey,
    ],
  );

  const applyBrandUserUpdate = useCallback(
    (brandId: number, updatedUser: BrandUser) => {
      const brandUserId = updatedUser.brandUserId;

      const updateBrandUsers = (brand: Brand): Brand => {
        if (brand.brandId !== brandId) return brand;
        const users = brand.brandUsers ?? [];
        const hasUser = users.some((u) => u.brandUserId === brandUserId);
        if (!hasUser) return brand;
        const nextUsers = users.map((u) =>
          u.brandUserId === brandUserId ? updatedUser : u,
        );
        return Brand.withProps(brand, { brandUsers: nextUsers });
      };

      const patchOrgBrands = (brands?: Brand[]): Brand[] => {
        if (!brands?.length) return brands ?? [];
        if (!brandsContainBrandId(brands, brandId)) return brands;
        return brands.map(updateBrandUsers);
      };

      // Patch persistent organizationDetails cache entries
      persistentDetailsCache.forEach((org, key) => {
        if (!org.brands?.length) return;
        if (!brandsContainBrandId(org.brands, brandId)) return;
        const brands = patchOrgBrands(org.brands);
        const next = cloneOrganizationWithBrands(org, brands);
        setCachedDetails(key, next);
      });

      setOrganizationDetails((prev) => {
        if (!prev?.brands?.length) return prev;
        const brands = patchOrgBrands(prev.brands);
        return cloneOrganizationWithBrands(prev, brands);
      });

      // Patch organizations list cache/state if brands include brandUsers
      setOrganizationsData((prev) => {
        if (!prev?.items?.length) return prev;
        const items = (prev.items as unknown[]).map((org: unknown) => {
          const o = org as { brands?: Brand[] };
          const orgBrands = o.brands ?? [];
          if (!brandsContainBrandId(orgBrands, brandId)) return org;
          const brands = patchOrgBrands(orgBrands);
          return { ...o, brands };
        });
        const next = { ...prev, items } as GetOrganizationsResult;
        setCachedPage(
          organizationsCurrentPage,
          organizationsItemsPerPage,
          next,
        );
        return next;
      });
    },
    [
      organizationsCurrentPage,
      organizationsItemsPerPage,
      setCachedDetails,
      setCachedPage,
      setOrganizationDetails,
    ],
  );

  const setOrganizationsCurrentPageStable = useCallback((page: number) => {
    setOrganizationsCurrentPage(page);
  }, []);

  const setOrganizationsItemsPerPageStable = useCallback((size: number) => {
    setOrganizationsItemsPerPage(size);
  }, []);

  const getKnownOrganizationKeys = useCallback((): ReadonlySet<string> => {
    return collectKnownOrganizationKeys(organizationsData);
  }, [organizationsData]);

  const getKnownBrandKeys = useCallback((): ReadonlySet<string> => {
    return collectKnownBrandKeys(organizationsData, organizationDetails);
  }, [organizationsData, organizationDetails]);

  const applyOrganizationCreate = useCallback(
    (created: Organization) => {
      setCachedDetails(created.organizationKey, created);

      setOrganizationsData((prev) => {
        if (!prev) {
          const next: GetOrganizationsResult = {
            items: [created],
            total: 1,
            page: organizationsCurrentPage,
            limit: organizationsItemsPerPage,
          };
          setCachedPage(
            organizationsCurrentPage,
            organizationsItemsPerPage,
            next,
          );
          return next;
        }

        const alreadyListed = prev.items.some(
          (org) =>
            org.organizationId === created.organizationId ||
            org.organizationKey === created.organizationKey,
        );
        if (alreadyListed) return prev;

        const nextTotal = prev.total + 1;
        const onFirstPage = organizationsCurrentPage === 1;
        let items = prev.items;

        if (onFirstPage) {
          items = [created, ...prev.items];
          if (items.length > organizationsItemsPerPage) {
            items = items.slice(0, organizationsItemsPerPage);
          }
        }

        const next: GetOrganizationsResult = {
          ...prev,
          items,
          total: nextTotal,
        };
        setCachedPage(
          organizationsCurrentPage,
          organizationsItemsPerPage,
          next,
        );
        return next;
      });
    },
    [
      organizationsCurrentPage,
      organizationsItemsPerPage,
      setCachedDetails,
      setCachedPage,
    ],
  );

  const applyBrandDetailsSave = useCallback(
    (updatedBrand: Brand, routeOrganizationKey?: string) => {
      const brandId = updatedBrand.brandId;
      setCachedBrandDetails(brandId, updatedBrand);

      const replaceBrand = (existing: Brand): Brand => {
        const nextFeatures =
          updatedBrand.brandFeatures ?? existing.brandFeatures;
        return Brand.withProps(existing, {
          brandKey: updatedBrand.brandKey,
          brandName: updatedBrand.brandName,
          brandDisplayName: updatedBrand.brandDisplayName,
          brandDescription: updatedBrand.brandDescription,
          brandLogoUrl: updatedBrand.brandLogoUrl,
          brandIsActive: updatedBrand.brandIsActive,
          brandUpdatedAt: updatedBrand.brandUpdatedAt,
          brandFeaturesCount:
            updatedBrand.brandFeaturesCount ??
            countActiveBrandFeatures(nextFeatures),
          brandFeatures: nextFeatures,
          brandUsers: existing.brandUsers ?? updatedBrand.brandUsers,
        });
      };

      const mergeOrgBrands = (org: Organization): Organization => {
        if (!org.brands?.length) return org;
        if (!brandsContainBrandId(org.brands, brandId)) return org;
        const brands = mapBrandsWithUpdatedOne(org.brands, brandId, replaceBrand);
        return cloneOrganizationWithBrands(org, brands);
      };

      persistentDetailsCache.forEach((org, key) => {
        const next = mergeOrgBrands(org);
        if (next !== org) setCachedDetails(key, next);
      });

      setOrganizationDetails((prev) => {
        if (!prev) return prev;
        const next = mergeOrgBrands(prev);
        const cacheKey =
          routeOrganizationKey?.trim() ||
          organizationDetailsKey ||
          prev.organizationKey;
        if (cacheKey) setCachedDetails(cacheKey, next);
        return next;
      });

      setOrganizationsData((prev) => {
        if (!prev?.items?.length) return prev;
        const items = prev.items.map((org) => {
          const orgBrands = org.brands ?? [];
          if (!brandsContainBrandId(orgBrands, brandId)) return org;
          const brands = mapBrandsWithUpdatedOne(orgBrands, brandId, replaceBrand);
          return cloneOrganizationWithBrands(org, brands);
        });
        const next = { ...prev, items } as GetOrganizationsResult;
        setCachedPage(
          organizationsCurrentPage,
          organizationsItemsPerPage,
          next,
        );
        return next;
      });
    },
    [
      organizationDetailsKey,
      organizationsCurrentPage,
      organizationsItemsPerPage,
      setCachedBrandDetails,
      setCachedDetails,
      setCachedPage,
      setOrganizationDetails,
    ],
  );

  const applyBrandCreate = useCallback(
    (organizationKey: string, created: Brand) => {
      setCachedBrandDetails(created.brandId, created);

      const appendBrandToOrg = (org: Organization): Organization => {
        const brands = org.brands ?? [];
        const alreadyListed = brands.some(
          (b) =>
            b.brandId === created.brandId || b.brandKey === created.brandKey,
        );
        if (alreadyListed) {
          const nextBrands = brands.map((b) =>
            b.brandId === created.brandId ? created : b,
          );
          return cloneOrganizationWithBrands(org, nextBrands);
        }
        return cloneOrganizationWithBrands(org, [...brands, created]);
      };

      const cachedOrg =
        getCachedDetails(organizationKey) ?? organizationDetails;
      if (cachedOrg) {
        setCachedDetails(organizationKey, appendBrandToOrg(cachedOrg));
      }

      setOrganizationDetails((prev) => {
        if (
          !prev ||
          !organizationKeysMatch(prev.organizationKey, organizationKey)
        ) {
          return prev;
        }
        return appendBrandToOrg(prev);
      });

      setOrganizationsData((prev) => {
        if (!prev?.items?.length) return prev;
        const items = prev.items.map((org) =>
          organizationKeysMatch(org.organizationKey, organizationKey)
            ? appendBrandToOrg(org)
            : org,
        );
        const next = { ...prev, items } as GetOrganizationsResult;
        setCachedPage(
          organizationsCurrentPage,
          organizationsItemsPerPage,
          next,
        );
        return next;
      });
    },
    [
      organizationDetails,
      organizationsCurrentPage,
      organizationsItemsPerPage,
      getCachedDetails,
      setCachedBrandDetails,
      setCachedDetails,
      setCachedPage,
      setOrganizationDetails,
    ],
  );

  const value = useMemo<OrganizationsSlice>(
    () => ({
      data: organizationsData,
      setData: setOrganizationsData,
      loading: organizationsLoading,
      setLoading: setOrganizationsLoading,
      error: organizationsError,
      setError: setOrganizationsError,
      currentPage: organizationsCurrentPage,
      setCurrentPage: setOrganizationsCurrentPageStable,
      itemsPerPage: organizationsItemsPerPage,
      setItemsPerPage: setOrganizationsItemsPerPageStable,
      getCachedPage,
      setCachedPage,
      organizationDetails,
      setOrganizationDetails,
      organizationDetailsLoading,
      setOrganizationDetailsLoading,
      organizationDetailsError,
      setOrganizationDetailsError,
      organizationDetailsKey,
      setOrganizationDetailsKey,
      getCachedDetails,
      setCachedDetails,
      getCachedBrandDetails,
      setCachedBrandDetails,
      applyBrandUpdate,
      applyBrandUserUpdate,
      getKnownOrganizationKeys,
      getKnownBrandKeys,
      applyOrganizationCreate,
      applyBrandCreate,
      applyBrandDetailsSave,
    }),
    [
      organizationsData,
      organizationsLoading,
      organizationsError,
      organizationsCurrentPage,
      setOrganizationsCurrentPageStable,
      organizationsItemsPerPage,
      setOrganizationsItemsPerPageStable,
      getCachedPage,
      setCachedPage,
      organizationDetails,
      organizationDetailsLoading,
      organizationDetailsError,
      organizationDetailsKey,
      getCachedDetails,
      setCachedDetails,
      getCachedBrandDetails,
      setCachedBrandDetails,
      applyBrandUpdate,
      applyBrandUserUpdate,
      getKnownOrganizationKeys,
      getKnownBrandKeys,
      applyOrganizationCreate,
      applyBrandCreate,
      applyBrandDetailsSave,
    ],
  );

  // Clear persistent caches when navigating away (provider unmounts).
  useEffect(() => {
    return () => {
      clearPersistentPageCache();
      clearPersistentDetailsCache();
      clearPersistentBrandDetailsCache();
    };
  }, []);

  return (
    <OrganizationsDataContext.Provider value={value}>
      {children}
    </OrganizationsDataContext.Provider>
  );
}
